import React from 'react';
import InteractivePopover from './InteractivePopover';

// Detect whether a translation row was edited by a linguist after the AI
// suggested its initial translation. The backend stamps original_translation
// on the row when the linguist saves an edit through the review modal.
export function isRequestModified(request) {
  if (!request) return false;
  const original = request.original_translation;
  const current = request.target || request.Spanish;
  return Boolean(original && current && original !== current);
}

function formatDate(dateString) {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (err) {
    return '';
  }
}

function formatDateTime(dateString) {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (err) {
    return '';
  }
}

function ModifiedBadge({ request }) {
  if (!request) return null;

  const isEdited = isRequestModified(request);
  const hasClassification = Boolean(request.error_category || request.severity);

  if (!isEdited) {
    if (hasClassification) {
      return (
        <InteractivePopover
          className="attribution-tag-popover interactive-popover-trigger--badge"
          popoverClassName="modified-badge-popover"
          trigger={(
            <span
              className="attribution-tag attribution-tag--ai"
              tabIndex={0}
              aria-label={`AI translation. ${request.error_category || ''} ${request.severity || ''}`.trim()}
            >
              AI
            </span>
          )}
        >
          {request.error_category && (
            <div className="modified-badge-tooltip-row">
              <div className="modified-badge-tooltip-label">Error Category</div>
              <div className="modified-badge-tooltip-value">{request.error_category}</div>
            </div>
          )}
          {request.severity && (
            <div className="modified-badge-tooltip-row">
              <div className="modified-badge-tooltip-label">Severity</div>
              <div className="modified-badge-tooltip-value">{request.severity}</div>
            </div>
          )}
        </InteractivePopover>
      );
    }

    return (
      <span
        className="attribution-tag attribution-tag--ai"
        title="AI translation - not yet edited by a linguist"
        aria-label="AI translation"
      >
        AI
      </span>
    );
  }

  const reviewer = request.reviewed_by ? request.reviewed_by.split('@')[0] : 'a linguist';
  const reviewedAt = formatDate(request.reviewed_at);
  const original = request.original_translation || '';
  const modified = request.target || request.Spanish || '';
  const editHistory = request.edit_history || [];
  const editCount = editHistory.length || 1;

  const a11yLabel = [
    `Linguist edited by ${reviewer}${reviewedAt ? ` on ${reviewedAt}` : ''}.`,
    `Original AI translation: ${original}`,
    `Current translation: ${modified}`,
    editHistory.length > 1 ? `${editHistory.length} edits total.` : '',
  ].filter(Boolean).join('\n');

  return (
    <InteractivePopover
      className="interactive-popover-trigger--badge"
      popoverClassName="modified-badge-popover"
      maxHeight={240}
      trigger={(
        <span
          className="attribution-tag attribution-tag--linguist modified-badge"
          tabIndex={0}
          aria-label={a11yLabel}
        >
          <span className="modified-badge-icon" aria-hidden="true">✎</span>
          <span className="modified-badge-label">
            Linguist edit{editCount > 1 ? ` (${editCount})` : ''}
          </span>
        </span>
      )}
    >
      <div className="modified-badge-tooltip-header">
        {editCount > 1 ? `Edit History (${editCount} edits)` : `Edited by ${reviewer}`}
        {editCount === 1 && reviewedAt ? ` on ${reviewedAt}` : ''}
      </div>

      <div className="modified-badge-tooltip-row modified-badge-tooltip-row--ai">
        <div className="modified-badge-tooltip-label">Original AI Translation</div>
        <div className="modified-badge-tooltip-value">{original}</div>
      </div>

      {editHistory.length > 0 ? (
        <div className="modified-badge-tooltip-history">
          {editHistory.map((edit, idx) => {
            const editorName = edit.edited_by ? edit.edited_by.split('@')[0] : 'Unknown';
            const editTime = formatDateTime(edit.edited_at);
            return (
              <div key={idx} className="modified-badge-tooltip-row modified-badge-tooltip-row--edit">
                <div className="modified-badge-tooltip-label">
                  Edit #{idx + 1} by {editorName}
                  {editTime ? ` · ${editTime}` : ''}
                </div>
                <div className="modified-badge-tooltip-value">{edit.translation}</div>
                {edit.notes && (
                  <div className="modified-badge-tooltip-notes">Note: {edit.notes}</div>
                )}
                {edit.error_category && (
                  <div className="modified-badge-tooltip-notes">
                    {edit.error_category} · {edit.severity || '—'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="modified-badge-tooltip-row">
          <div className="modified-badge-tooltip-label">Current Translation</div>
          <div className="modified-badge-tooltip-value">{modified}</div>
        </div>
      )}
    </InteractivePopover>
  );
}

export default ModifiedBadge;
